import React, { useEffect, useMemo, useRef, useState } from "react";
import { useUser } from "@clerk/clerk-react";
import {
  useGetVQAActivity,
  useGetAssesmentRequestDetails,
  useDocumentFile,
  useDocumentAllFiles,
  useGetRFIsDetails,
  useGetClientSecrets,
  useGetRFIActivity,
  useAcceptVendorQuestionnaire,
  useExportQuestionnaireExcel,
  useDownloadExportFile,
  useDownloadExportDocs,
  useSupportingDocsExportVQA,
  useGetAutoreleaseEligibility,
  useAutoreleaseVqq,
  useCancelQuestionnaireRequest,
  useGetRfiQuestionnaireSnapshot,
  useGetRfiScoredSnapshot,
} from "../../custom-hooks/queries";
import { useIsOrgRiskScoringEnabled } from "../../utils/orgFeatureGates";
import { vqqRiskScoringRequested } from "../../utils/vqqRiskAddOns";
import RiskHeaderBadge from "./readonly/RiskHeaderBadge";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  ArrowDownTrayIcon,
  CalendarDaysIcon,
  CheckBadgeIcon,
  ChevronRightIcon,
  FolderPlusIcon,
  PaperClipIcon,
} from "@heroicons/react/24/outline";
import { Icon } from "@iconify/react/dist/iconify.js";
import LOGO from "../../assets/images/diligentLogoShortavif.avif";
import SkeletonLoader from "../../components/SkeletonLoader";
import MarkdownText from "../../components/MarkdownText";

import Skeleton from "react-loading-skeleton";
import {
  dateFormat,
  calculateDaysAgo,
  useUserRole,
  isDiligentAdminUserRole,
  resolveGxpCodesFromRfi,
  stripGxpFromName,
  getTemplateNameOnly,
  formatVqqName,
  appendVqqLocation,
  isAutoreleaseAudience,
  isPreAcceptanceStatus,
} from "../../utils/helperMethods";
import { pastSubmissionSnapshotId } from "../../utils/autoreleaseSnapshot";
import AutoreleasePanel, {
  AutoreleasePanelSkeleton,
} from "./autorelease/AutoreleasePanel";
import SegmentSelectorGrid from "./autorelease/SegmentSelectorGrid";
import LastSubmittedResponsesHeader from "./autorelease/LastSubmittedResponsesHeader";
import AutoreleasedBadge from "../../components/Badges/AutoreleasedBadge";
import GxpBadge from "../../components/RFIsComponents/GxpBadge";
import RfiTemplateServiceBadges from "../../components/RFIs/RfiTemplateServiceBadges";
import RfiTypeBadge from "../../components/RFIs/RfiTypeBadge";
import VqqResponsesReadOnly from "./readonly/VqqResponsesReadOnly";
import RiskOverviewCard from "./readonly/RiskOverviewCard";
import DiligentModelTooltip from "./readonly/DiligentModelTooltip";
import {
  isRfiCancelledStatus,
  canShowRfiResponsesTab,
  computeRfiDetailsExportState,
} from "../../utils/rfiRequestActionPermissions";
import StatusProgressBar from "../../components/ProgressBar";
import CustomButton from "../../components/CustomButton";
import Modal from "../../components/Modal";
import Pagination from "../../components/CustomPagination";
import StatusForDrawer from "../../components/StatusForDrawer";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { ReportContainer } from "../../components/ReportContainer";
import DataLoading from "../../components/Skeletons/DataLoader";
import { ResponseTab } from "./ResponstTab";
import CustomDropdown from "../../components/RFIsComponents/Common/CustomDropdown";
import { StatusBadges } from "../../components/StatusBadges";
import { Button } from "@headlessui/react";
import AdminDetailsLoader from "../../components/Skeletons/adminDetailsLoader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExcel, faFilePdf } from "@fortawesome/free-regular-svg-icons";
import { VendorQuestionnaireAccept } from "../../components/RFIsComponents/VendorQuestionnaireAccept";
import { use } from "react";
import ExportMenu from "../../components/Menus/ExportMenu";
import moment from "moment";
import Notification from "../../components/Notification";
import DownloadNotifications from "../../components/Notifications/DownloadNotifications";
import JSZip from "jszip";
import { getLocationFromProvider } from "../../utils/addressUtils";
import {
  formatRequestContactNameEmail,
  getRequestContactsForDetails,
} from "../../utils/requestContactFormUtils";

/**
 * 14030 — Page-level Risk Badge rendered in the VQQ Details header so the
 * RAG + score are visible on every tab (Overview, Activity, Responses,
 * Audit Logs, etc.). Identical visual to the in-Responses-tab badge so users
 * see a consistent risk indicator no matter where they are in the page.
 */
const RISK_HEADER_BADGE_CLASS = {
  Red: "bg-red-50 text-red-700 border-red-200",
  Yellow: "bg-yellow-50 text-yellow-700 border-yellow-200",
  Green: "bg-green-50 text-green-700 border-green-200",
};

export const RFIDetails = () => {
  const user = useUserRole();
  const diligentAdmin = isDiligentAdminUserRole(user);
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { datePassed } = location.state || {};
  const [downloadNotificationOpen, setDownloadNotificationOpen] =
    useState(false);
  const [fileType, setFileType] = useState("");
  const [downloadFileName, setDownloadFileName] = useState("");
  const [exportLogsId, setExportLogsId] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState("overview");
  const [showInstructions, setShowInstructions] = useState(false);

  const { data: clientSecrets } = useGetClientSecrets();
  const [message, setMessage] = useState("");
  const [show, setShow] = useState(false);

  const { data: rfisDetails, isLoading, refetch } = useGetRFIsDetails(id);
  const { data: activity, refetch: activityRefetch } = useGetRFIActivity(
    rfisDetails?.sponsorRfiResponseId
  );
  const vendorRequestContacts = useMemo(
    () => getRequestContactsForDetails(rfisDetails),
    [rfisDetails]
  );

  // 14030 — Page-level Risk Badge. Visible across all tabs (Overview, Activity,
  // Responses, etc.) so the headline RAG + score are always one glance away.
  // Two hops:
  //   1) The questionnaire snapshot endpoint resolves HC questionnaire_id (the
  //      URL param) → DocumentService rfi_questionnaire_id (Guid).
  //   2) /RiskScoring/snapshot/{guid} returns the latest scoring run header
  //      (overall_score + overall_tier).
  // Gated on entitlement + Complete status: non-Complete VQQs or non-entitled
  // viewers see no badge.
  // useIsOrgRiskScoringEnabled is called UNCONDITIONALLY (rules of hooks) and
  // then combined with the admin bypass in a plain expression.
  const headerIsRiskEnabled = useIsOrgRiskScoringEnabled();
  // A VQQ that never asked for a risk add-on, custom VQQs included, shows no
  // risk surface to anyone: no header badge, no Risk Overview card, no risk exports.
  const headerCanSeeRisk = (diligentAdmin || headerIsRiskEnabled) && vqqRiskScoringRequested(rfisDetails);
  const headerCompletedVqq = rfisDetails?.status === "Complete";
  const { data: headerQuestionnaireSnapshot } = useGetRfiQuestionnaireSnapshot(
    headerCompletedVqq && headerCanSeeRisk ? id : null
  );
  const { data: headerScoredSnapshot } = useGetRfiScoredSnapshot(
    headerQuestionnaireSnapshot?.rfi_questionnaire_id,
    {
      enabled:
        !!headerQuestionnaireSnapshot?.rfi_questionnaire_id && headerCanSeeRisk,
      // 14033 — sponsor + vendor only see published runs. Admin sees latest.
      publishedOnly: !diligentAdmin,
    }
  );

  // Risk surface activation, mirroring VqqResponsesReadOnly. mode is resolved
  // from purchase entitlement, so admin-backfilled scores on non-purchased
  // VQQs come back with mode="None" even though a scoring run exists. Treat
  // that as risk-active for admins (so the tab label and risk-augmented
  // exports light up) while keeping sponsor/vendor gated by purchase mode.
  const headerHasRiskSurface =
    !!headerScoredSnapshot &&
    (
      (!!headerScoredSnapshot.mode && headerScoredSnapshot.mode !== "None") ||
      (diligentAdmin && !!headerScoredSnapshot.scoring_run_id)
    );

  const [comment, setComment] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [isRequested, setIsRequested] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalDescription, setModalDescription] = useState("");
  const [searchParams] = useSearchParams();
  const responses = searchParams.get("page");
  // 13876 — when arriving from an autorelease panel link, the past-submission Details page
  // should breadcrumb back to the pending request rather than the user's grid.
  const returnTo = searchParams.get("returnTo");
  // Fetch the pending VQQ's details so the breadcrumb can show both levels with their names.
  const { data: pendingVqqDetails } = useGetRFIsDetails(returnTo || null);
  const { mutate: exportExcel } = useExportQuestionnaireExcel();
  const {
    mutate: exportToExcel,
    data: exportData,
    isError: exportExcelError,
  } = useDownloadExportFile();

  const {
    mutate: exportToDocs,
    data: exportDocsData,
    isError: exportDocsError,
  } = useDownloadExportDocs();

  // Supporting documents only — feeds the "Supporting Documents" entry in the
  // export menu. Builds a ZIP client-side from the document URLs the BE returns.
  const {
    mutate: downloadSupportDocs,
    data: supportDocsData,
    isError: supportDocsError,
  } = useSupportingDocsExportVQA();

  const handleExportExcel = (type) => {
    const encodedQuestionnaireName = encodeURIComponent(rfisDetails?.name);
    const data = {
      entity_id: rfisDetails?.questionnaire_id,
      entity_type: "QuestionnaireResponse",
      purchase_vendor: rfisDetails?.vendorName,
      sponsor_name: rfisDetails?.sponsorName,
      export_format: type,
      org_id: rfisDetails?.organizationId,
      questionnaire_name: encodedQuestionnaireName,
      released_date: moment().toISOString(),
      subtype: "test",
    };

    exportExcel(data, {
      onSuccess: (response) => {
        setDownloadFileName(response?.file_name || rfisDetails?.name || "");
        setExportLogsId(response?.export_logs_id);
        setFileType(`rfifiles-${type}`);
        setDownloadNotificationOpen(true);
      },
      onError: (error) => {
        console.error("Export Excel failed:", error);
      },
    });
  };

  const handleAllDocuments = (type) => {
    const encodedQuestionnaireName = encodeURIComponent(rfisDetails?.name);
    const data = {
      entity_id: rfisDetails?.questionnaire_id,
      entity_type: "QuestionnaireResponse",
      purchase_vendor: rfisDetails?.vendorName,
      sponsor_name: rfisDetails?.sponsorName,
      export_format: type,
      org_id: rfisDetails?.organizationId,
      questionnaire_name: encodedQuestionnaireName,
      released_date: moment().toISOString(),
      subtype: "alldocuments",
    };

    exportExcel(data, {
      onSuccess: (response) => {
        setDownloadFileName(response?.file_name || rfisDetails?.name || "");
        setExportLogsId(response?.export_logs_id);
        setFileType(`rfidocs-${type}`);
        setDownloadNotificationOpen(true);
      },
      onError: (error) => {
        console.error("Export Excel failed:", error);
      },
    });
  };

  const handleExportPartial = (type) => {
    const encodedQuestionnaireName = encodeURIComponent(rfisDetails?.name);
    const data = {
      entity_id: rfisDetails?.questionnaire_id,
      entity_type: "QuestionnaireResponse",
      purchase_vendor: rfisDetails?.vendorName,
      sponsor_name: rfisDetails?.sponsorName,
      export_format: type,
      org_id: rfisDetails?.organizationId,
      questionnaire_name: encodedQuestionnaireName,
      released_date: moment().toISOString(),
      subtype: "test",
      is_partial: true,
    };

    exportExcel(data, {
      onSuccess: (response) => {
        setDownloadFileName(response?.file_name || rfisDetails?.name || "");
        setExportLogsId(response?.export_logs_id);
        setFileType(`rfifiles-${type}`);
        setDownloadNotificationOpen(true);
      },
      onError: (error) => {
        console.error("Export Partial failed:", error);
      },
    });
  };

  // 14155 — Augmented Excel: bundles vendor responses + Risk Model anchors +
  // per-finding analysis + methodology + live-formula summary. Same queue +
  // entity as the responses-only export; the subtype routes the worker to the
  // RiskAugmentedExcelBuilder instead of the standard one.
  const handleExportRiskAugmented = (type) => {
    const encodedQuestionnaireName = encodeURIComponent(rfisDetails?.name);
    const data = {
      entity_id: rfisDetails?.questionnaire_id,
      entity_type: "QuestionnaireResponse",
      purchase_vendor: rfisDetails?.vendorName,
      sponsor_name: rfisDetails?.sponsorName,
      export_format: type,
      org_id: rfisDetails?.organizationId,
      questionnaire_name: encodedQuestionnaireName,
      released_date: moment().toISOString(),
      subtype: "risk_augmented",
    };

    exportExcel(data, {
      onSuccess: (response) => {
        setDownloadFileName(response?.file_name || rfisDetails?.name || "");
        setExportLogsId(response?.export_logs_id);
        setFileType(`rfifiles-${type}`);
        setDownloadNotificationOpen(true);
      },
      onError: (error) => {
        console.error("Export Risk Augmented failed:", error);
      },
    });
  };

  // Supporting documents — fires the BE call to list the VQA documents; the
  // useEffect below zips them client-side once the data lands.
  const handleSupportingDocs = () => {
    if (!rfisDetails?.sponsorRfiResponseId) {
      console.warn("Supporting docs: no sponsorRfiResponseId on rfisDetails");
      return;
    }
    downloadSupportDocs({
      entityId: rfisDetails.sponsorRfiResponseId,
      entityType: "Questionnaire",
    });
  };

  const lastSupportDocsBatchRef = useRef(null);
  useEffect(() => {
    if (supportDocsError) return;
    const items = supportDocsData?.items;
    if (!items || !items.length) return;
    // Same data twice in a row would otherwise re-download. Hash by length +
    // first item id; cheap and good enough for this surface.
    const batchKey = `${items.length}:${items[0]?.document_id ?? ""}`;
    if (lastSupportDocsBatchRef.current === batchKey) return;
    lastSupportDocsBatchRef.current = batchKey;

    const zip = new JSZip();
    Promise.all(
      items.map((doc) =>
        fetch(doc.document_url)
          .then((res) => res.blob())
          .then((blob) => zip.file(doc.document_name || `document-${doc.document_id}`, blob))
          .catch((err) => console.error("Supporting docs: failed to fetch", doc.document_url, err))
      )
    ).then(() => {
      zip.generateAsync({ type: "blob" }).then((zipBlob) => {
        const url = window.URL.createObjectURL(zipBlob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${rfisDetails?.name || "supporting-documents"}-${moment().format("YYYY-MM-DD")}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      });
    });
  }, [supportDocsData, supportDocsError, rfisDetails?.name]);

  const lastZipDownloadedRef = useRef(null);

  useEffect(() => {
    const downloadLink = exportDocsData?.download_link;

    if (!downloadLink || exportExcelError) return;
    if (lastZipDownloadedRef.current === downloadLink) return;
    lastZipDownloadedRef.current = downloadLink;

    fetch(downloadLink)
      .then((res) => {
        if (!res.ok) throw new Error("Network response was not ok");

        const contentDisposition = res.headers.get("Content-Disposition");
        let fileName = "document";

        if (contentDisposition?.includes("filename=")) {
          const match = contentDisposition.match(/filename="?(.+?)"?$/);
          if (match?.[1]) {
            fileName = match[1].split(".")[0];
          }
        }

        return res.blob().then((blob) => ({ blob, fileName }));
      })
      .then(({ blob, fileName }) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        // Prefer the backend-computed export name (already normalized); the raw
        // rfisDetails.name still carries the "Diligent - ... RFI" branding.
        link.download = `${downloadFileName || formatVqqName(rfisDetails?.name) || fileName}.zip`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => {
        console.error("ZIP download failed:", error);
      });
  }, [exportDocsData?.download_link, exportExcelError]);

  const lastExportDownloadedRef = useRef(null);

  useEffect(() => {
    const downloadLink = exportData?.download_link;

    if (!downloadLink || exportExcelError) return;
    if (lastExportDownloadedRef.current === downloadLink) return;
    lastExportDownloadedRef.current = downloadLink;

    fetch(downloadLink)
      .then((res) => {
        if (!res.ok) throw new Error("Network response was not ok");

        const contentDisposition = res.headers.get("Content-Disposition");
        const contentType = res.headers.get("Content-Type");

        let fileName = "document";
        let fileExtension = "xlsx"; // default

        if (contentDisposition?.includes("filename=")) {
          const match = contentDisposition.match(/filename="?(.+?)"?$/);
          if (match?.[1]) {
            const parts = match[1].split(".");
            fileName = parts[0];
            fileExtension = parts[1] || "xlsx";
          }
        } else if (downloadLink.includes("?")) {
          const match = downloadLink.match(/\.([a-zA-Z0-9]+)(\?|$)/);
          if (match?.[1]) {
            fileExtension = match[1];
          }
        }

        return res.blob().then((blob) => ({ blob, fileName, fileExtension }));
      })
      .then(({ blob, fileName, fileExtension }) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${downloadFileName || formatVqqName(rfisDetails?.name) || fileName}.${fileExtension}`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => {
        console.error("File download failed:", error);
      });
  }, [exportData?.download_link, exportExcelError]);

  const [isAccepting, setIsLoading] = useState(false);

  // Handler for closing the modal
  const handleModalClose = () => {
    navigate("/RequestAssesmentList");
  };

  // const {
  //   mutate,
  //   data: files,
  //   isError: isFileError,
  //   error: errorMessageFile,
  // } = useDocumentFile();

  // const {
  //   mutate: allFilesDownload,
  //   data: allFiles,
  //   isError: allFilesError,
  // } = useDocumentAllFiles();

  // const handleDownload = (id) => {
  //   mutate(id, {
  //     onSuccess: () => {
  //       setShow(true);
  //       setMessage("Downloaded Successfully");
  //     },
  //     onError: () => {
  //       setShow(true);
  //       setMessage("Download Failed");
  //     },
  //   });
  // };

  // const handleDownloadAll = async (ids) => {
  //   allFilesDownload(ids); // Pass ids directly instead of using docsIds (state update is async)
  // };

  // Filter documents based on whether any sponsor has cancelled

  // Tab switching handler
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const [updateNotificationOpen, setUpdateNotificationOpen] = useState(false);
  const [notificationStatus, setNotificationStatus] = useState("success");

  const [iframeUrl, setIframeUrl] = useState("");

  const resolvedQuestionnaireId =
    rfisDetails?.questionnaire_id ??
    (id !== undefined && id !== null && id !== ""
      ? Number(id)
      : undefined);

  const showResponsesTab = canShowRfiResponsesTab(
    user,
    rfisDetails?.status,
    rfisDetails?.questionnaire_accepted,
    rfisDetails
  );

  useEffect(() => {
    if (responses === "responses" && showResponsesTab) {
      setActiveTab("responses");
    }
  }, [responses, showResponsesTab]);

  useEffect(() => {
    if (!rfisDetails) return;

    const url =
      user === "Provider User"
        ? `/questionnaires/${rfisDetails.purchase_vendor_questionnaire_id}?hideNavigation=true`
        : `/due-diligence/${rfisDetails.purchaseVendorId}/questionnaires/${resolvedQuestionnaireId}?hideNavigation=true`;

    setIframeUrl(url);
  }, [rfisDetails, user, resolvedQuestionnaireId]);

  const { mutate: acceptQuestionnaire, isError } =
    useAcceptVendorQuestionnaire();

  // 13876 Autorelease — only vendors / CRO-in-vendor-portal see this UI.
  const { user: clerkUser } = useUser();
  const subRoles = clerkUser?.publicMetadata?.sub_role ?? [];
  const autoreleaseAudience = isAutoreleaseAudience(user, subRoles);
  const autoreleasePreAcceptance = isPreAcceptanceStatus(rfisDetails?.status);
  const autoreleaseCheckEnabled =
    autoreleaseAudience &&
    autoreleasePreAcceptance &&
    rfisDetails?.is_global_template === true;

  const { data: autoreleaseEligibility, isLoading: isEligibilityLoading } =
    useGetAutoreleaseEligibility(
      rfisDetails?.sponsorRfiResponseId,
      autoreleaseCheckEnabled
    );
  const autoreleaseEligible = autoreleaseEligibility?.eligible === true;

  const [selectedPastId, setSelectedPastId] = useState(null);
  const selectedPast = useMemo(() => {
    if (!autoreleaseEligibility?.past_submissions) return null;
    return (
      autoreleaseEligibility.past_submissions.find(
        (p) => p.sponsor_rfi_response_id === selectedPastId
      ) ?? null
    );
  }, [autoreleaseEligibility, selectedPastId]);

  // Default-selection: location-match > single-submission > none (multi-segment + no match).
  useEffect(() => {
    if (!autoreleaseEligible) return;
    if (selectedPastId) return;
    const list = autoreleaseEligibility?.past_submissions ?? [];
    if (list.length === 0) return;
    const match = list.find((p) => p.location_matches_current_request);
    if (match) {
      setSelectedPastId(match.sponsor_rfi_response_id);
    } else if (list.length === 1) {
      setSelectedPastId(list[0].sponsor_rfi_response_id);
    }
  }, [autoreleaseEligible, autoreleaseEligibility, selectedPastId]);

  const { mutate: autoreleaseVqq, isPending: isAutoreleasePending } =
    useAutoreleaseVqq();
  const { mutate: cancelVqqRequest, isPending: isCancelPending } =
    useCancelQuestionnaireRequest();

  const handleAutoreleaseConfirmed = (past, closeModal) => {
    if (!past || !rfisDetails?.sponsorRfiResponseId) return;
    autoreleaseVqq(
      {
        sponsorRfiResponseId: rfisDetails.sponsorRfiResponseId,
        pastSponsorRfiResponseId: past.sponsor_rfi_response_id,
      },
      {
        onSuccess: () => {
          closeModal?.();
          setNotificationStatus("success");
          setMessage("VQQ Autoreleased successfully");
          setUpdateNotificationOpen(true);
          refetch();
        },
        onError: (err) => {
          closeModal?.();
          setNotificationStatus("error");
          setMessage(
            err?.response?.data?.message ||
              err?.message ||
              "Autorelease failed — please try again."
          );
          setUpdateNotificationOpen(true);
        },
      }
    );
  };

  const handleCancelRequestConfirmed = (closeModal) => {
    if (!resolvedQuestionnaireId) return;
    cancelVqqRequest(
      { id: resolvedQuestionnaireId },
      {
        onSuccess: () => {
          closeModal?.();
          setNotificationStatus("success");
          setMessage("Request cancelled");
          setUpdateNotificationOpen(true);
          refetch();
        },
        onError: (err) => {
          closeModal?.();
          setNotificationStatus("error");
          setMessage(
            err?.response?.data?.message ||
              err?.message ||
              "Cancel failed — please try again."
          );
          setUpdateNotificationOpen(true);
        },
      }
    );
  };

  const handleViewLastSubmitted = () => {
    setActiveTab("responses");
  };

  const handleSubmit = () => {
    setIsLoading(true);
    acceptQuestionnaire(
      {
        questionnaire_id: resolvedQuestionnaireId,
        data: { email: rfisDetails?.vendor_contact_email },
      },
      {
        onSuccess: () => {
          setNotificationStatus("success");
          setMessage("Questionnaire accepted successfully");
          setUpdateNotificationOpen(true);
          refetch();
          setTimeout(() => {
            setIsLoading(false);

            navigate(
              `/RFI/${resolvedQuestionnaireId}/Details?page=responses`
            );
          }, 1000);
        },
        onError: (error) => {
          setIsLoading(false);
          setNotificationStatus("error");
          setMessage(
            error?.response?.data?.message || "Error accepting questionnaire"
          );
          setUpdateNotificationOpen(true);
        },
      }
    );
  };

  return (
    <div className="container">
      <div className="p-7 border-y border-gray-300 bg-slate-50 mb-5">
        <div className="flex items-center text-sm">
          <h1
            className="text-gray-500 cursor-pointer font-semibold hover:text-gray-700"
            onClick={() =>
              navigate(
                diligentAdmin ? "/Backend/Rfis" : user === "Provider User" ? "/RFI/Requests" : "/RFI/History"
              )
            }
          >
            My VQQs
          </h1>
          <ChevronRightIcon className="w-5 h-5 text-gray-400" />
          {returnTo ? (
            <>
              <button
                type="button"
                onClick={() => navigate(`/RFI/${returnTo}/Details`)}
                className="text-gray-500 font-semibold hover:text-gray-700 truncate max-w-xs"
                title={formatVqqName(pendingVqqDetails?.name) || "Pending request"}
              >
                {formatVqqName(pendingVqqDetails?.name) || "Pending request"}
              </button>
              <ChevronRightIcon className="w-5 h-5 text-gray-400 shrink-0" />
              <span
                className="text-gray-900 font-semibold truncate max-w-xs"
                title={formatVqqName(rfisDetails?.name)}
              >
                {formatVqqName(rfisDetails?.name) || "Loading…"}
              </span>
            </>
          ) : null}
        </div>
        <div className="mt-5 space-y-2 flex  flex-col justify-start items-start md:justify-between md:items-center md:flex-row">
          <div className="flex items-center">
            <span className="bg-white border border-gray-300 rounded-md px-7 py-5 mr-5">
              {/* <CheckBadgeIcon className="h-9 w-9 text-red-500" /> */}
              <img src={LOGO} alt="Logo" className="h-10 w-10" />
            </span>

            <span>
              <p className="text-md font-semibold">{rfisDetails?.vendorName}</p>
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm text-gray-500">{appendVqqLocation(formatVqqName(stripGxpFromName(rfisDetails?.name)), rfisDetails?.locationCity, rfisDetails?.locationState)}</p>
                {resolveGxpCodesFromRfi(rfisDetails).map((code) => (
                  <GxpBadge key={code} code={code} />
                ))}
              </div>
              <p className="text-sm text-gray-500">
                {dateFormat(rfisDetails?.requestDate)}
              </p>

              <p className="text-sm">
                <span className="font-semibold">
                  {rfisDetails?.RequestDate}
                </span>
              </p>
            </span>
          </div>

          <div>
            {" "}
            {!rfisDetails?.questionnaire_accepted &&
              user === "Provider User" &&
              (autoreleaseCheckEnabled && isEligibilityLoading ? (
                <AutoreleasePanelSkeleton />
              ) : autoreleaseEligible ? (
                <AutoreleasePanel
                  eligibility={autoreleaseEligibility}
                  selectedPast={selectedPast}
                  rfisDetails={rfisDetails}
                  onViewLastSubmitted={handleViewLastSubmitted}
                  onAcceptAndUpdate={handleSubmit}
                  onAutoreleaseConfirmed={handleAutoreleaseConfirmed}
                  onCancelRequestConfirmed={handleCancelRequestConfirmed}
                  isAutoreleasePending={isAutoreleasePending}
                  isCancelPending={isCancelPending}
                />
              ) : !isRfiCancelledStatus(rfisDetails?.status, rfisDetails) ? (
                <CustomButton
                  color={isAccepting ? "bg-gray-300" : "bg-indigo-600"}
                  size="text-base"
                  weight="font-normal"
                  height="h-9"
                  width="w-48"
                  textColor="text-white"
                  border={"border border-gray-300"}
                  text={isAccepting ? "Accepting..." : "Accept Questionnaire"}
                  onClick={() => {
                    handleSubmit();
                  }}
                  borderRadius="0.375rem"
                  disabled={isAccepting}
                />
              ) : null)}
          </div>

          {/* 14030 — Big stacked Risk Score pushed to the far right of the page header. */}
          {headerCanSeeRisk && headerScoredSnapshot ? (
            <div className="ml-auto">
              <RiskHeaderBadge snapshot={headerScoredSnapshot} />
            </div>
          ) : null}
        </div>

        <div className="mt-5 flex flex-col md:flex-row md:justify-between items-start md:items-center space-y-2"></div>
      </div>{" "}
      {/* Tab Navigation */}
      <div className="border-b border-gray-200 px-5">
        <nav className="-mb-px flex items-center justify-between">
          <div className="flex justify-between items-center w-full">
            <div>
              <button
                onClick={() => handleTabChange("overview")}
                className={`whitespace-nowrap py-4 px-6 border-b-2 font-medium text-sm ${
                  activeTab === "overview"
                    ? "border-indigo-600 text-indigo-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Overview
              </button>
              {(showResponsesTab || autoreleaseEligible) && (
                <button
                  onClick={() => handleTabChange("responses")}
                  className={`whitespace-nowrap py-4 px-6 border-b-2 font-medium text-sm ${
                    activeTab === "responses"
                      ? "border-indigo-600 text-indigo-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  {autoreleaseEligible && !rfisDetails?.questionnaire_accepted
                    ? "Last Submitted Responses"
                    : headerHasRiskSurface
                      ? "Questionnaire & Risk Analysis"
                      : "Questionnaire"}
                </button>
              )}
            </div>
            <div className="flex flex-row items-center space-x-2">
              {(() => {
                // The page's single export menu. Options and visibility come
                // from one shared computation so risk-augmented entries,
                // archive transparency and the partial-export acceptance gate
                // behave identically wherever the menu is rendered.
                const { options, canExport } = computeRfiDetailsExportState({
                  status: rfisDetails?.status, details: rfisDetails, userRole: user,
                  isDiligentAdmin: diligentAdmin, canSeeRisk: headerCanSeeRisk, scoredSnapshot: headerScoredSnapshot,
                });
                return canExport ? (
                  <ExportMenu
                    exportExcel={handleExportExcel}
                    exportDocs={handleAllDocuments}
                    exportPartial={handleExportPartial}
                    exportRiskAugmented={handleExportRiskAugmented}
                    exportZipAll={handleAllDocuments}
                    exportSupportingDocs={handleSupportingDocs}
                    options={options}
                    notification={downloadNotificationOpen}
                    setNotification={setDownloadNotificationOpen}
                    fileType={fileType}
                    details={rfisDetails}
                    downloadFileName={downloadFileName}
                    exportLogsId={exportLogsId}
                  />
                ) : null;
              })()}
            </div>
          </div>
          {/* <span className="sm:mr-0 lg:mr-[12.8rem]">
            <CustomDropdown />
          </span> */}
        </nav>
      </div>
      {/* Tab Content */}
      {activeTab === "overview" &&
        (isLoading ? (
          <div className="w-full md:w-[70%] border-r border-gray-300 px-3 md:mr-5 lg:mr-8  ">
            <AdminDetailsLoader />
          </div>
        ) : (
          <>
            {/* 14030 — Risk Overview card sits ABOVE the two-column layout
                so the Status/Template/etc. details and the VQQ Activity
                rail both start beneath it. Card spans the full overview
                width; renders only when the user is entitled to risk and
                a scored snapshot exists. */}
            {headerCanSeeRisk ? (
              <div className="px-5 pt-4">
                <RiskOverviewCard
                  scoredSnapshot={headerScoredSnapshot}
                  methodology={null}
                  templateName={rfisDetails?.name}
                />
              </div>
            ) : null}
            <div className="mb-28 px-5 flex justify-center md:justify-between items-stretch flex-col md:flex-row">
            <div className="w-full md:w-[90%] border-r border-gray-300 px-3 mr-8">
              <div className="divide-y divide-gray-300">
                <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                  <h2 className="text-sm font-medium text-gray-900">Status </h2>
                  <div className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <StatusBadges statusName={rfisDetails?.status} />
                      {rfisDetails?.isAutoreleased ? <AutoreleasedBadge /> : null}
                    </div>
                  </div>
                </div>

                {isRfiCancelledStatus(rfisDetails?.status, rfisDetails) && (
                  <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                    <h2 className="text-sm font-medium text-gray-900">Notice</h2>
                    <p className="mt-1 text-sm text-gray-600 sm:col-span-2 sm:mt-0">
                      This request has been cancelled. Questionnaire responses
                      and acceptance are not available until the request is
                      reactivated.
                    </p>
                  </div>
                )}

                {diligentAdmin && rfisDetails?.status === "Archived" ? (
                  <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                    <h2 className="text-sm font-medium text-gray-900">
                      Archive Comment
                    </h2>
                    <p className="mt-1 text-sm text-gray-600 sm:col-span-2 sm:mt-0 italic">
                      {(rfisDetails?.archiveComment ?? "").trim() || "—"}
                    </p>
                  </div>
                ) : null}

                <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                  <h2 className="text-sm font-medium text-gray-900">
                    Template
                  </h2>
                  <div className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    <RfiTemplateServiceBadges
                      services={rfisDetails?.services ?? rfisDetails?.Services}
                      fallbackTemplateName={getTemplateNameOnly(rfisDetails?.name)}
                    />
                  </div>
                </div>

                <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                  <h2 className="text-sm font-medium text-gray-900">Type</h2>
                  <div className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    <RfiTypeBadge item={rfisDetails} />
                  </div>
                </div>

                <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                  <h2 className="text-sm font-medium text-gray-900">
                    Location
                  </h2>
                  <p className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    {getLocationFromProvider({
                      address: rfisDetails?.location,
                      city: rfisDetails?.locationCity,
                      state: rfisDetails?.locationState,
                      country: rfisDetails?.locationCountry
                    }, { short: false, abbvState: true }) || rfisDetails?.location}
                  </p>
                </div>

                <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                  <h2 className="text-sm font-medium text-gray-900">
                    Sponsor Contact
                  </h2>
                  <p className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    <span className="flex items-center gap-x-2">
                      <Icon
                        icon="fa-solid:address-book"
                        className="w-4 h-4 text-black-600"
                      />
                      {rfisDetails?.sponsor_contact_email}
                    </span>
                  </p>
                </div>

                <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                  <h2 className="text-sm font-medium text-gray-900">
                    Vendor Contact
                  </h2>
                  <p className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0 space-y-1">
                    {vendorRequestContacts.length > 0 ? (
                      vendorRequestContacts.map((contact, index) => (
                        <span
                          key={contact?.email || index}
                          className="flex items-center gap-x-2"
                        >
                          <Icon
                            icon="fa-solid:address-book"
                            className="w-4 h-4 text-black-600"
                          />
                          {formatRequestContactNameEmail(contact)}
                        </span>
                      ))
                    ) : (
                      <span className="flex items-center gap-x-2">
                        <Icon
                          icon="fa-solid:address-book"
                          className="w-4 h-4 text-black-600"
                        />
                        -
                      </span>
                    )}
                  </p>
                </div>
                <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                  <h2 className="text-sm font-medium text-gray-900">
                    Request Date
                  </h2>
                  <p className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    <span className="flex items-center gap-x-2">
                      <CalendarDaysIcon className="w-4 h-4 text-indigo-600" />
                      {dateFormat(rfisDetails?.requestDate)}
                    </span>
                  </p>
                </div>
                <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                  <h2 className="text-sm font-medium text-gray-900">
                    Release Date
                  </h2>
                  <p className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    <span className="flex items-center gap-x-2">
                      <CalendarDaysIcon className="w-4 h-4 text-indigo-600" />
                      {rfisDetails?.initialReleaseDate ||
                      rfisDetails?.initial_release_date
                        ? dateFormat(
                            rfisDetails?.initialReleaseDate ||
                              rfisDetails?.initial_release_date
                          )
                        : "-"}
                    </span>
                  </p>
                </div>
                <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                  <h2 className="text-sm font-medium text-gray-900">
                    Completion Date
                  </h2>
                  <p className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    <span className="flex items-center gap-x-2">
                      <CalendarDaysIcon className="w-4 h-4 text-indigo-600" />
                      {rfisDetails?.releaseDate
                        ? dateFormat(rfisDetails?.releaseDate)
                        : "-"}
                    </span>
                  </p>
                </div>
                <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                  <h2 className="text-sm font-medium text-gray-900">
                    Expected Due Date
                  </h2>
                  <p className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    <span className="flex items-center gap-x-2">
                      <CalendarDaysIcon className="w-4 h-4 text-indigo-600" />
                      {rfisDetails?.due_by
                        ? dateFormat(rfisDetails?.due_by)
                        : "-"}
                    </span>
                  </p>
                </div>
                <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                  <h2 className="text-sm font-medium text-gray-900">
                    Additional Info
                  </h2>
                  <p className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    {rfisDetails?.additionalInfo}
                  </p>
                </div>
              </div>
            </div>

            {/* Right column: Reports and Assessment Activity */}
            <div className="w-full md:w-[30%] mt-10 md:mt-0 mb-20">
              <div></div>
              <div className="mt-10 md:mt-6">
                <h2 className="text-sm font-semibold text-gray-900 ml-2">
                  VQQ Activity
                </h2>
                <ul className="mt-6 space-y-6 h-80 px-2">
                  {activity
                    ?.slice()
                    ?.sort((a, b) => {
                      // First sort by date (descending)
                      const dateA = new Date(a.activity_done_on);
                      const dateB = new Date(b.activity_done_on);
                      const dateDiff = dateB - dateA;

                      // If dates are the same (or very close), sort by activity sequence
                      if (Math.abs(dateDiff) < 1000) {
                        // Less than 1 second difference
                        const getActivityOrder = (description) => {
                          if (description.includes("Sent Questionnaire"))
                            return 1;
                          if (
                            description.includes(
                              "Status changed from Sent to In Progress"
                            )
                          )
                            return 2;
                          if (
                            description.includes(
                              "Status changed from In Progress to In Review"
                            )
                          )
                            return 3;
                          if (
                            description.includes(
                              "Status changed from In Review to In Remediation"
                            )
                          )
                            return 4;
                          if (
                            description.includes(
                              "Status changed from In Remediation to Completed"
                            )
                          )
                            return 5;
                          if (
                            description.includes("reaffirmed as In Remediation")
                          )
                            return 6;
                          if (
                            description.includes(
                              "Status changed from In Review to Complete"
                            )
                          )
                            return 7;
                          return 0; // Unknown activities go first
                        };

                        return (
                          getActivityOrder(b.activity_description) -
                          getActivityOrder(a.activity_description)
                        );
                      }

                      return dateDiff;
                    })
                    ?.map((item, index, array) => {
                      // Get the latest activity (most recent date)
                      const isLatest =
                        item?.activity_done_on ===
                        array.reduce((latest, current) =>
                          new Date(current?.activity_done_on) >=
                          new Date(latest?.activity_done_on)
                            ? current
                            : latest
                        )?.activity_done_on;

                      // Format date as 'Month DD, YYYY'
                      const formattedDate = item?.activity_done_on
                        ? new Date(item.activity_done_on).toLocaleDateString(
                            "en-US",
                            {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            }
                          )
                        : "-";

                      return (
                        <li key={index} className="relative flex gap-x-4">
                          {/* Line connecting items */}
                          <div className="absolute left-0 top-0 flex w-6 justify-center -bottom-6">
                            <div className="w-px bg-gray-200"></div>
                          </div>

                          {/* Circle or Profile Picture */}
                          <div className="relative flex h-6 w-6 items-center justify-center bg-white">
                            {item?.activity_comments && !isLatest ? (
                              <div className="h-6 w-6 rounded-full bg-gray-200 flex items-center justify-center ring-2 ring-gray-300">
                                <span className="text-xs font-medium text-gray-700">
                                  {item?.activity_done_by
                                    ?.charAt(0)
                                    .toUpperCase() || "A"}
                                </span>
                              </div>
                            ) : (
                              <div className="absolute right-0 bottom-0 text-xs text-green-500">
                                <CheckCircleIcon className="size-6 absolute right-0 bottom-0 text-green-600" />
                              </div>
                            )}
                          </div>

                          {/* Activity Description and Comment */}
                          <div className="flex-[1_1_0%]">
                            <p className="py-0.5 text-xs text-gray-500">
                              <span className="font-medium text-gray-900">
                                {!item?.activity_comments &&
                                  item?.activity_done_by}
                              </span>{" "}
                              {item?.activity_description}
                            </p>
                            {item?.activity_comments && (
                              <div className="mt-2 rounded-md border border-gray-300 p-3 text-sm text-gray-800 shadow-sm w-[110%]">
                                <div className="flex justify-between">
                                  <span className="font-medium text-gray-900 text-xs">
                                    {item?.activity_done_by}{" "}
                                    <span className="py-0.5 text-xs leading-5 text-gray-500 font-normal">
                                      commented
                                    </span>
                                  </span>
                                  <time
                                    dateTime={item?.activity_done_on}
                                    className="flex-none py-0.5 text-xs text-gray-500"
                                  >
                                    {formattedDate}
                                  </time>
                                </div>
                                <div className="text-gray-500 text-sm mt-2">
                                  {item?.activity_comments}
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Activity Time */}
                          <time
                            dateTime={item?.activity_done_on}
                            className="flex-none py-0.5 text-xs text-gray-500"
                          >
                            {!item?.activity_comments && formattedDate}
                          </time>
                        </li>
                      );
                    })}
                </ul>
              </div>
            </div>
          </div>
          </>
        ))}
      {/* Template Instructions (Responses tab): collapsible panel above the iframe. */}
      {(activeTab === "responses" ||
        (responses === "responses" && activeTab !== "overview")) &&
      rfisDetails?.instructions ? (
        <div className="px-4 pt-4">
          <div className="border border-gray-200 rounded-md bg-white">
            <button
              type="button"
              onClick={() => setShowInstructions((v) => !v)}
              className="w-full flex items-center justify-between px-4 py-3 text-left"
              aria-expanded={showInstructions}
            >
              <span className="text-sm font-medium text-gray-900">
                Template Instructions
              </span>
              <ChevronRightIcon
                className={`w-5 h-5 text-gray-400 shrink-0 transition-transform ${
                  showInstructions ? "rotate-90" : ""
                }`}
              />
            </button>
            {showInstructions ? (
              <div className="px-4 pb-4 pt-1 border-t border-gray-100">
                <MarkdownText text={rfisDetails.instructions} />
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* Response Tab Content */}
      {(activeTab === "responses" ||
        (responses === "responses" && activeTab !== "overview")) &&
        (autoreleaseEligible && !rfisDetails?.questionnaire_accepted ? (
          <div className="px-4 py-4 space-y-4">
            <LastSubmittedResponsesHeader
              pastSubmissions={autoreleaseEligibility?.past_submissions ?? []}
              currentSponsorName={
                autoreleaseEligibility?.current_sponsor_name ?? rfisDetails?.sponsorName
              }
              returnToId={id}
            />
            {(autoreleaseEligibility?.past_submissions?.length ?? 0) >= 2 ? (
              <SegmentSelectorGrid
                segments={autoreleaseEligibility.past_submissions}
                selectedId={selectedPastId}
                onSelect={setSelectedPastId}
                returnToId={id}
              />
            ) : null}
            {selectedPast?.questionnaire_id ? (
              <VqqResponsesReadOnly
                questionnaireId={pastSubmissionSnapshotId(selectedPast)}
                rfisDetails={rfisDetails}
              />
            ) : (
              <div className="bg-white border border-gray-200 rounded p-6 text-sm text-gray-500">
                Select a previously submitted segment above to view those responses.
              </div>
            )}
          </div>
        ) : rfisDetails?.status === "Complete" ? (
          <VqqResponsesReadOnly
            questionnaireId={
              rfisDetails?.autoreleasedFromQuestionnaireId ?? id
            }
            rfisDetails={rfisDetails}
          />
        ) : showResponsesTab ? (
          <ResponseTab
            activeTab={activeTab}
            clientSecrets={clientSecrets}
            route={iframeUrl}
            heading={
              user === "Provider User" ? "View Questionnaire" : "View Responses"
            }
            desc={user === "Provider User" ? "questionnaire" : "responses"}
          />
        ) : null)}
      <Notification
        show={updateNotificationOpen}
        setShow={setUpdateNotificationOpen}
        status={notificationStatus}
        errorMessage={message}
      />{" "}
      <Modal
        open={openModal}
        setOpen={setOpenModal}
        onClick={handleModalClose}
        is_requested={isRequested}
        title={modalTitle}
        description={modalDescription}
        textBtn="Assesments Request List"
        vqaDetailsRefetch={handleModalClose}
      />
    </div>
  );
};

export default RFIDetails;
